import { useEffect, useState, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { getAge } from "@/utils/getAge";

import useTitanApp from "@/hooks/useTitanApp";
import {
  addNewPatient,
  updatePatientDetails,
  setSelectedPatientDetails,
  getPatientByKeyword,
  setSearchedDoctors,
  getDoctorsBySearch,
  getCompanyTags,
  setSelectedCompanyTags,
  addCompanyTags,
  deleteCompanyTags,
  setIsPatientDashboard,
  getPatientsListV2,
  openPatientForm,
  getPatientByDemoGraphics,
  getPatientByNhsNumber,
  setNewPatientFormDefaults,
  getPatientConsultationByConsultationId,
  getPatientByPatientId,
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
} from "@/dispatchers/patientDispatchers";
import {
  updateConsultationForPatientMatch,
  getReferralDataByConsultationId,
} from "@/dispatchers/pharmacyServiceDispatchers";
import {
  setPatientInfoTab,
  setisPatientSearched,
  setCurrentGlobalTabValue,
  setCurrentGlobalTabText,
} from "@/dispatchers/appDispatchers";
import {
  setSelectedTemplate,
  setIsTemplateSelected,
} from "@/dispatchers/templateDispatchers";
import { severityStringToNumber } from "../allergyData";
import { therapyLevelStringToNumber } from "../medicationsData";
import { statusStringToNumber } from "../ConditionsData";
import { useNavigate } from "react-router";
import {
  searchPDSByName,
  searchPDSByNHS,
  validateNHSNumber,
  formatNHSNumber,
  PDSPatient,
} from "@/services/pdsService";
import { IntercomTrack } from "@/data/IntercomTrack";
import { intercomActionPatientAddText } from "@/data/constantStrings";
import { ethnicityTypes } from "@/enum/ethnicityType";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";

// Types and Interfaces
interface PatientAllergy {
  id: string;
  allergyName: string;
  severity: "mild" | "moderate" | "severe";
  typicalReaction?: string;
  additionalNotes?: string;
}

interface PatientCondition {
  id: string;
  conditionName: string;
  status: "chronic" | "active" | "resolved";
  diagnosedDate?: string;
  additionalNotes?: string;
}

export const statusMap: Record<number | string, string> = {
  1: "chronic",
  2: "active",
  3: "resolved",
};

interface PatientMedication {
  id: string;
  itemName: string;
  dose: string;
  times?: {
    morning?: string;
    midday?: string;
    evening?: string;
    night?: string;
  };
  quantity?: string;
  measure?: string;
  therapyLevel: "acute" | "chronic" | "maintenance" | "asneeded";
  preparation?: string;
  additionalNotes?: string;
}

export const therapyLevelMap: Record<number | string, string> = {
  1: "chronic",
  2: "acute",
  3: "maintenance",
  4: "asneeded",
};

export const relationshipOptions = [
  { value: "1", label: "Emergency Contact" },
  { value: "2", label: "Parent" },
  { value: "3", label: "Guardian" },
  { value: "4", label: "Spouse" },
  { value: "5", label: "Partner" },
  { value: "6", label: "Child" },
  { value: "7", label: "Sibling" },
  { value: "8", label: "Friend" },
  { value: "9", label: "Relative" },
  { value: "10", label: "Caregiver" },
  { value: "11", label: "Other" },
];

interface PatientRelationship {
  id: string;
  patientId: string;
  patientName: string;
  patientPhone: string;
  patientEmail: string;
  patientAddress: string;
  relationshipType: string;
  isPrimary?: boolean;
}

interface Doctor {
  gpName: string;
  surgeryName: string;
  surgeryAddress: string;
}

interface PatientTag {
  tagId?: string;
  companyId?: string;
  tagName: string;
  tagDescription?: string;
  colour?: string;
  createdAt?: string;
  createdBy?: string;
  createdByEmail?: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByEmail?: string;
  status: TagStatus;
}

enum TagStatus {
  Available = 1,
  Archived = 2,
}

interface PatientPreferredAddress {
  houseName: string;
  streetName: string;
  locality: string;
  town: string;
  postcode: string;
  county: string;
}

interface PatientFormData {
  title: string;
  name: string;
  dateOfBirth: string;
  gender: string;
  nhsNumber: string;
  telephone: string;
  email: string;
  address: string;
  ethnicity: string;
  allergies: PatientAllergy[];
  conditions: PatientCondition[];
  medications: PatientMedication[];
  patientTags: [];
  additionalTags: PatientTag[];
  doctor: Doctor;
  relationships: PatientRelationship[];
  givenName: string;
  middleName: string;
  familyName: string;
  preferredAddress: PatientPreferredAddress;
  surgeryPhone?: string;
  surgeryEmail?: string;
  doctorName?: string;
  surgeryData?: any;
  gpData?: any;
}

interface PatientFormErrors {
  name?: string;
  dateOfBirth?: string;
  gender?: string;
  nhsNumber?: string;
  telephone?: string;
  email?: string;
  address?: string;
  ethnicity?: string;
  relationships?: string;
  givenName?: string;
  familyName?: string;
}

interface AddPatientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (patient: any) => void;
  isDarkMode?: boolean;
  editMode?: any;
}

interface Allergy {
  id: string;
  patientId: string;
  name: string;
  severity: string;
  reaction: string;
  dateAdded: string;
}

interface Condition {
  id: string;
  patientId: string;
  name: string;
  severity: string;
  dateAdded: string;
  status: "active" | "inactive" | "resolved";
}

interface Medication {
  id: string;
  patientId: string;
  name: string;
  dosage: string;
  frequency: string;
  startDate: string;
  endDate?: string;
  prescribedBy: string;
}

interface Vital {
  id: string;
  patientId: string;
  type: "heart-rate" | "weight" | "blood-pressure" | "temperature";
  value: string;
  unit: string;
  timestamp: string;
  recordedBy: string;
}

interface Patient {
  id: string;
  name: string;
  nhsNumber: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  address: string;
  gender: string;
  ethnicity: string;
  allergies: Allergy[];
  conditions: Condition[];
  medications: Medication[];
  vitals: Vital[];
  consultations: Consultation[];
}

interface Consultation {
  id: string;
  patientId: string;
  date: string;
  time: string;
  pharmacist: string;
  summary: string;
  actions: ConsultationAction[];
  status: "scheduled" | "in-progress" | "completed" | "cancelled";
}

interface ConsultationAction {
  type: "prescription" | "payment" | "appointment" | "referral" | "vaccine";
  description: string;
  amount?: string;
  date?: string;
}

// Default Data
export const getInitialFormData = (): PatientFormData => ({
  title: "",
  name: "",
  givenName: "",
  middleName: "",
  familyName: "",
  dateOfBirth: "",
  gender: "",
  nhsNumber: "",
  telephone: "",
  email: "",
  address: "",
  ethnicity: "",
  allergies: [],
  conditions: [],
  medications: [],
  patientTags: [],
  additionalTags: [],
  doctor: {
    gpName: "",
    surgeryName: "",
    surgeryAddress: "",
  },
  relationships: [],
  preferredAddress: {
    houseName: "",
    streetName: "",
    locality: "",
    town: "",
    postcode: "",
    county: "",
  },
  surgeryData: null,
  gpData: null,
});

// Custom Hook for Patient Form State Management
export const useAddPatientFormData = ({
  open,
  onOpenChange,
  onSave,
  isDarkMode,
  editMode,
}: any) => {
  const { appState, patientState } = useTitanApp();
  const {
    darkMode,
    companyId,
    currentPharmacyId,
    currentUser,
    currentPharmacy,
    company,
    features,
  } = appState || {};
  const {
    updatePatientDetailsLoader,
    addPatientLoader,
    showPatientDetailsEditModal,
    searchedDoctors,
    isCompanyTagsLoading,
    companyTags,
    selectedCompanyTags,
    searchedSurgeries,
    surgeryData,
    newPatientFormDefaults,
  } = patientState || {};

  const [formData, setFormData] = useState<PatientFormData>(
    getInitialFormData(),
  );
  const [newMedication, setNewMedication] = useState("");
  const [newTag, setNewTag] = useState("");
  const isInitializing = useRef(false);
  const preservedPatientIdsRef = useRef<{ patientID?: string; _id?: string }>(
    {},
  ); // PRESERVE only IDs
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState("");
  const [addPatientFormMessage, setAddPatientFormMessage] = useState("");
  const [activeTab, setActiveTab] = useState("personal");
  const [errors, setErrors] = useState<PatientFormErrors>({});
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState<() => void>(() => () => {});
  const [deleteMessage, setDeleteMessage] = useState(
    "Are you sure you want to delete this item?",
  );

  const [isOpenTagsPickerDropdown, setIsOpenTagsPickerDropdown] = useState(
    false,
  );
  const [tagsOptions, setTagsOptions] = useState(null);
  const [tagFilterValue, setTagFilterValue] = useState("");
  const [previewTagColor, setPreviewTagColor] = useState("");
  const [editingTag, setEditingTag] = useState(null);
  const [currentTag, setCurrentTag] = useState(null);
  const [isFromPDS, setIsFromPDS] = useState(false);
  const [nhsEmergencyConsultationId, setNhsEmergencyConsultationId] = useState(
    "",
  );

  //PDS States
  const [currentStep, setCurrentStep] = useState<"pds-search" | "form-entry">(
    "pds-search",
  );
  const [pdsSearchMode, setPDSSearchMode] = useState<"name" | "nhs">("name");
  const [isPDSSearching, setIsPDSSearching] = useState(false);
  const [pdsSearchResults, setPDSSearchResults] = useState<PDSPatient[]>([]);
  const [pdsSearchError, setPDSSearchError] = useState<string>("");

  // PDS Name Search fields
  const [pdsGivenName, setPDSGivenName] = useState("");
  const [pdsFamilyName, setPDSFamilyName] = useState("");
  const [pdsGender, setPDSGender] = useState("");
  const [pdsDOB, setPDSDOB] = useState("");
  const [pdsPostcode, setPDSPostcode] = useState("");

  // PDS NHS Search fields
  const [pdsNHSNumber, setPDSNHSNumber] = useState("");
  const [pdsNHSDOB, setPDSNHSDOB] = useState("");

  // Verification/edit mode flags (optional - copied from builder)
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<
    "success" | "failed" | null
  >(null);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [givenNameLocal, setGivenNameLocal] = useState("");
  const [familyNameLocal, setFamilyNameLocal] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    setTagsOptions(companyTags);
  }, [companyTags]);

  const navigateToNhsConsultation = async (
    patientId: string,
    consultationId: string,
  ) => {
    const consultationResponse = await getPatientConsultationByConsultationId(
      consultationId,
    );
    const consultationData = consultationResponse as any;

    await getPatientByPatientId(patientId);
    await getReferralDataByConsultationId(consultationId);

    setFreeTypeMode(false);
    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes !== null);
    setPatientNotes(consultationData?.notes ?? "");
    setConsultationPaused(consultationData?.consultationStatus === 3);
    setShowPlayPauseBtn(consultationData?.consultationStatus === 3);
    setisHandleStartConsultation(false);

    if (
      consultationData?.template != null &&
      (consultationData?.consultationStatus === 2 ||
        consultationData?.consultationStatus === 3)
    ) {
      setIsTemplateSelected(true);
    }

    if (
      consultationData?.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }

    setPreConsultationTemplate(consultationData?.preConsultationTemplate);

    if (consultationData?.preConsultationTemplate) {
      let preConsultTranscription = "";

      consultationData.preConsultationTemplate.templateSections?.forEach(
        (section) => {
          section?.userControls?.forEach((control) => {
            if (control.label && control.value) {
              preConsultTranscription += ` ${control.label} ${control.value} `;
            }
          });
        },
      );

      setPreConsultationTranscription(preConsultTranscription.trim());
    } else {
      setPreConsultationTranscription("");
    }

    setCurrentGlobalTabValue(features?.ifPharmacyDashboardEnabled ? 4 : 3);
    setCurrentGlobalTabText("Patients");
    setisPatientSearched(true);

    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultationId}`,
    );

    setPatientInfoTab(1);

    if (consultationData?.consultationChat) {
      try {
        const parsedAiChatMessages = JSON.parse(
          consultationData.consultationChat,
        );
        setAiChatSuccessMessages();
        parsedAiChatMessages.forEach((message) => {
          setAddPatientAIChatMessage(message);
        });
      } catch (err) {
        console.error("Invalid consultationChat JSON:", err);
      }
    }
  };

  const generateRandomTagsColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = 70 + Math.random() * 20;
    const lightness = 45 + Math.random() * 10;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const toggleTagsPickerDropdown = async (e) => {
    e.stopPropagation();

    setIsOpenTagsPickerDropdown((prev) => !prev);

    if (!isOpenTagsPickerDropdown) {
      getCompanyTags(companyId);
    }
  };

  // Business Logic Functions
  const handleInputChange = (
    field: keyof PatientFormData,
    value: string,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({ ...formData, [field]: value });
  };

  const closeModal = () => {
    openPatientForm(false);
    setNewPatientFormDefaults(null);
  };

  const normalizeDateForInput = (value?: string) => {
    if (!value) return "";

    const rawValue = String(value).trim();
    if (!rawValue) return "";

    if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
      return rawValue.split("T")[0];
    }

    const slashDateMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashDateMatch) {
      const [, day, month, year] = slashDateMatch;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    return "";
  };

  const formatNhsNumberForInput = (value?: string) => {
    const digits = String(value || "")
      .replace(/[^\d]/g, "")
      .slice(0, 10);

    return digits.replace(
      /(\d{3})(\d{3})(\d{0,4})/,
      (_, first, second, third) =>
        [first, second, third].filter(Boolean).join(" "),
    );
  };

  useEffect(() => {
    if (!open || editMode || !newPatientFormDefaults) return;

    const dateOfBirth = normalizeDateForInput(
      newPatientFormDefaults.dateOfBirth,
    );
    const nhsNumber = formatNhsNumberForInput(newPatientFormDefaults.nhsNumber);
    const familyName =
      newPatientFormDefaults.familyName || newPatientFormDefaults.surName || "";
    const sourceConsultationId =
      newPatientFormDefaults.nhsEmergencyConsultationId || "";

    setNhsEmergencyConsultationId(sourceConsultationId);

    setFormData((prev) => ({
      ...prev,
      ...newPatientFormDefaults,
      familyName,
      dateOfBirth,
      ethnicity:
        newPatientFormDefaults.ethnicity != null
          ? String(newPatientFormDefaults.ethnicity)
          : prev.ethnicity,
      nhsNumber: nhsNumber.replace(/\s/g, ""),
    }));
    setPDSGivenName(newPatientFormDefaults.givenName || "");
    setPDSFamilyName(familyName);
    setPDSDOB(dateOfBirth);
    setPDSPostcode(newPatientFormDefaults.postcode || "");
    setPDSNHSNumber(nhsNumber);
    setPDSNHSDOB(dateOfBirth);
    setPDSSearchMode(
      newPatientFormDefaults.pdsSearchMode || (nhsNumber ? "nhs" : "name"),
    );
    setPDSSearchResults([]);
    setPDSSearchError("");
    setCurrentStep("pds-search");
    setPDSGender(newPatientFormDefaults.sex || "");
    setNewPatientFormDefaults(null);
  }, [open, editMode, newPatientFormDefaults]);

  const handleSelectTag = (tag) => {
    const prev = Array.isArray(selectedCompanyTags) ? selectedCompanyTags : [];

    const selectedTagsFromList = prev.some((t) => t.tagName === tag.tagName)
      ? prev.filter((t) => t.tagName !== tag.tagName)
      : [...prev, tag];

    setSelectedCompanyTags(selectedTagsFromList);
  };

  useEffect(() => {
    if (!Array.isArray(tagsOptions) || !Array.isArray(selectedCompanyTags))
      return;

    const updatedTags = selectedCompanyTags.map((selectedTag) => {
      const latestTag = tagsOptions.find(
        (t) =>
          t.tagId === selectedTag.tagId ||
          t._id === selectedTag.tagId ||
          t.tagId === selectedTag._id,
      );
      return latestTag ? latestTag : selectedTag;
    });

    const hasChanges =
      JSON.stringify(updatedTags) !== JSON.stringify(selectedCompanyTags);

    if (hasChanges) {
      setSelectedCompanyTags(updatedTags);
    }
  }, [tagsOptions]);

  const handleAddCompanyTag = async (
    tagValue?: string,
    isEdit: boolean = false,
    tagId?: string,
    colorHex?: string,
  ) => {
    const tagNameToUse = tagValue?.trim() || tagFilterValue.trim();
    if (!tagNameToUse) return;

    const currentTagObj = Array.isArray(tagsOptions)
      ? tagsOptions.find((t) => t.tagId === tagId)
      : null;

    const exists = Array.isArray(tagsOptions)
      ? tagsOptions.find(
          (t) => t.tagName.toLowerCase() === tagNameToUse.toLowerCase(),
        )
      : null;

    if (isEdit && currentTagObj?.tagId) {
      const updatedTag = {
        ...currentTagObj,
        tagName: tagNameToUse,
        colour:
          colorHex || currentTagObj?.colour || previewTagColor || "#808080",
        companyId: companyId,
        status: 1,
      };

      try {
        const response = await addCompanyTags(updatedTag);
        const updatedFromServer = response?.data || response;

        setTagsOptions((prev) =>
          prev.map((t) =>
            t.tagId === currentTagObj.tagId ? updatedFromServer : t,
          ),
        );
      } catch (error) {}

      setTagFilterValue("");
      return;
    }

    let newTag = exists;
    if (!exists) {
      const newColor = previewTagColor || generateRandomTagsColor();
      newTag = {
        tagName: tagNameToUse,
        colour: newColor,
        tagId: uuidv4(),
        companyId: companyId,
        status: 1,
      };

      try {
        const response = await addCompanyTags(newTag);
        const createdTag = response?.data || response;

        setTagsOptions((prev) => [...(prev || []), createdTag]);

        handleSelectTag(createdTag);

        setCurrentTag(createdTag.tagName);
      } catch (error) {}
    } else {
      handleSelectTag(newTag);
    }

    setTagFilterValue("");
  };

  const handleDeleteTag = async (tagId) => {
    if (!tagId) return;

    try {
      const response = await deleteCompanyTags(tagId);

      const isSuccess =
        response === true ||
        (response?.success === true && response?.response === true);

      if (isSuccess) {
        setTagsOptions((prev) => prev.filter((t) => t.tagId !== tagId));
        setSelectedCompanyTags((prev) => prev.filter((t) => t.tagId !== tagId));
      }
    } catch (error) {}
  };

  // Allergy Handlers
  const handleSaveAllergy = (
    newAllergy: any,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    const allergy: PatientAllergy = {
      id: Date.now().toString(),
      allergyName: newAllergy.allergyName,
      severity: newAllergy.severity,
      typicalReaction: newAllergy.reaction || "",
      additionalNotes: newAllergy.notes || "",
    };
    setFormData({ ...formData, allergies: [...formData.allergies, allergy] });
  };

  const normalizeSeverity = (
    value: string | number | null,
  ): "mild" | "moderate" | "severe" | null => {
    if (value === null || value === undefined) return null;

    const val = typeof value === "string" ? value.toLowerCase() : value;

    switch (val) {
      case 1:
      case "1":
      case "mild":
        return "mild";
      case 2:
      case "2":
      case "moderate":
        return "moderate";
      case 3:
      case "3":
      case "severe":
        return "severe";
      default:
        return null;
    }
  };

  const handleEditAllergy = (
    id: string,
    updatedAllergy: any,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      allergies: formData.allergies.map((allergy) =>
        allergy.id === id
          ? {
              ...allergy,
              allergyName: updatedAllergy.allergyName,
              severity: normalizeSeverity(updatedAllergy.severity),
              typicalReaction: updatedAllergy.reaction || "",
              additionalNotes: updatedAllergy.notes || "",
            }
          : allergy,
      ),
    });
  };

  const handleDeleteAllergy = (
    allergyName: string,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      allergies: formData.allergies.filter(
        (allergy) => allergy.allergyName !== allergyName,
      ),
    });
  };

  // Condition Handlers
  const handleSaveCondition = (
    newCondition: any,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    const condition: PatientCondition = {
      id: Date.now().toString(),
      conditionName: newCondition.conditionName,
      status: newCondition.status,
      diagnosedDate: newCondition.diagnosedDate
        ? new Date(newCondition.diagnosedDate).toISOString()
        : null,
      additionalNotes: newCondition.notes || "",
    };
    setFormData({
      ...formData,
      conditions: [...formData.conditions, condition],
    });
  };

  const handleEditCondition = (
    id: string,
    updatedCondition: any,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.map((condition) =>
        condition.id === id
          ? {
              ...condition,
              conditionName: updatedCondition.conditionName,
              status: updatedCondition.status,
              diagnosedDate: updatedCondition.diagnosedDate
                ? new Date(updatedCondition.diagnosedDate).toISOString()
                : null,
              additionalNotes: updatedCondition.notes || "",
            }
          : condition,
      ),
    });
  };

  const handleDeleteCondition = (
    conditionName: string,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      conditions: formData.conditions.filter(
        (condition) => condition.conditionName !== conditionName,
      ),
    });
  };

  // Medication Handlers
  const handleSaveMedication = (
    newMedication: any,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    const medication: PatientMedication = {
      id: Date.now().toString(),
      itemName: newMedication.item,
      dose: newMedication.dose,
      times: newMedication.times || {
        morning: "",
        midday: "",
        evening: "",
        night: "",
      },
      quantity: newMedication.quantity || "",
      measure: newMedication.measure || "",
      therapyLevel: newMedication.therapyLevel,
      preparation: newMedication.preparation || "",
      additionalNotes: newMedication.notes || "",
    };
    setFormData({
      ...formData,
      medications: [...formData.medications, medication],
    });
  };

  const handleEditMedication = (
    id: string,
    updatedMedication: any,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      medications: formData.medications.map((medication) =>
        medication.id === id
          ? {
              ...medication,
              item: updatedMedication.item,
              dose: updatedMedication.dose,
              times: updatedMedication.times || {
                morning: "",
                midday: "",
                evening: "",
                night: "",
              },
              quantity: updatedMedication.quantity || "",
              measure: updatedMedication.measure || "",
              therapyLevel: updatedMedication.therapyLevel,
              preparation: updatedMedication.preparation || "",
              notes: updatedMedication.notes || "",
            }
          : medication,
      ),
    });
  };

  const handleDeleteMedication = (
    itemName: string,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      medications: formData.medications.filter(
        (medication) => medication.itemName !== itemName,
      ),
    });
  };

  //Doctors Handlers
  const handleDoctorSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchedDoctors([]);
      return;
    }

    try {
      const res = await getDoctorsBySearch(query);
      if (res?.data) {
        setSearchedDoctors(res.data);
      } else {
        setSearchedDoctors([]);
      }
    } catch (err) {
      console.error("Error searching doctors:", err);
      setSearchedDoctors([]);
    }
  };

  // Relationship Management Functions
  const addRelationship = (
    patient: {
      id: string;
      name: string;
      phone: string;
      email: string;
      address: string;
    },
    relationshipType: string,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    const newRelationship: PatientRelationship = {
      id: uuidv4(),
      patientId: patient.id,
      patientName: patient.name,
      patientPhone: patient.phone,
      patientEmail: patient.email,
      patientAddress: patient.address,
      relationshipType,
      isPrimary: formData.relationships.length === 0,
    };

    setFormData({
      ...formData,
      relationships: [...formData.relationships, newRelationship],
    });
  };

  const removeRelationship = (
    id: string,
    formData: PatientFormData,
    setFormData: (data: PatientFormData) => void,
  ) => {
    setFormData({
      ...formData,
      relationships: formData.relationships.filter((rel) => rel.id !== id),
    });
  };

  // Age Calculation Helper
  const calculateAge = (dateOfBirth: string): number => {
    if (!dateOfBirth) return 0;

    const today = new Date();
    const birthDate = new Date(dateOfBirth);

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // Check if patient requires guardian consent (under 16)
  const requiresGuardianConsent = (formData: PatientFormData): boolean => {
    const age = calculateAge(formData.dateOfBirth);
    return age < 16;
  };

  // Check if patient has parent or guardian relationship
  const hasParentOrGuardian = (formData: PatientFormData): boolean => {
    return formData.relationships.some(
      (rel) =>
        Number(rel.relationshipType) === 2 ||
        Number(rel.relationshipType) === 3,
    );
  };

  const validateNHSNumber = (nhsNumber: string): string | null => {
    if (!nhsNumber) {
      return;
    }
    if (!/^\d{10}$/.test(nhsNumber)) {
      return "Invalid NHS number format";
    }
    return null;
  };

  const validateUKTelephone = (telephone: string): string | null => {
    if (!telephone) {
      return "Telephone is required";
    }
    const cleanedNumber = telephone.replace(/[\s\-\(\)]/g, "");

    // Allow any international number starting with +
    if (cleanedNumber.startsWith("+")) {
      // Must have between 7 and 15 digits after the +
      if (/^\+\d{7,15}$/.test(cleanedNumber)) {
        return null;
      }
      return "Enter a valid international phone number (e.g., +923001234567)";
    }

    const ukPatterns = [
      /^(01\d{9})$/,
      /^(02\d{9})$/,
      /^(03\d{9})$/,
      /^(07\d{9})$/,
      /^(08\d{9})$/,
    ];

    const isValid = ukPatterns.some((pattern) => pattern.test(cleanedNumber));

    if (!isValid) {
      return "Enter a valid UK or international phone number (e.g., 07123456789 or +923001234567)";
    }

    return null;
  };

  const validateDateOfBirth = (dateOfBirth: string): string | null => {
    if (!dateOfBirth) {
      return "Date of Birth is required";
    }

    const dob = new Date(dateOfBirth);
    const today = new Date();

    if (dob > today) {
      return "Date of Birth cannot be in the future";
    }

    const maxAge = new Date();
    maxAge.setFullYear(today.getFullYear() - 150);

    if (dob < maxAge) {
      return "Please enter a valid Date of Birth";
    }

    return null;
  };

  const isFormValid = (formData: PatientFormData): boolean => {
    if (
      !formData.name.trim() ||
      !formData.dateOfBirth ||
      !formData.telephone.trim()
    ) {
      return false;
    }

    const nameParts = formData.name.trim().split(" ");
    if (nameParts.length < 2) {
      return false;
    }

    const dobError = validateDateOfBirth(formData.dateOfBirth);
    if (dobError) {
      return false;
    }

    const telephoneError = validateUKTelephone(formData.telephone);
    if (telephoneError) {
      return false;
    }

    if (formData.nhsNumber) {
      const nhsError = validateNHSNumber(formData.nhsNumber);
      if (nhsError) {
        return false;
      }
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return false;
    }

    if (requiresGuardianConsent(formData) && !hasParentOrGuardian(formData)) {
      return false;
    }

    return true;
  };

  const validateGuardianAge = async (
    relationships: PatientRelationship[],
    patientDateOfBirth: string,
    searchFunction?: (pharmacyId: string, query: string) => Promise<any[]>,
    pharmacyId?: string,
  ): Promise<string | null> => {
    const patientAge = calculateAge(patientDateOfBirth);

    if (patientAge >= 16) return null;
    const guardianRelationships = relationships.filter(
      (rel) =>
        Number(rel.relationshipType) === 2 ||
        Number(rel.relationshipType) === 3,
    );

    if (guardianRelationships.length === 0) {
      return "Patient under 16 must have a parent or guardian relationship";
    }

    if (searchFunction && pharmacyId) {
      for (const guardian of guardianRelationships) {
        try {
          const guardianResults = await searchFunction(
            pharmacyId,
            guardian.patientName,
          );
          const guardianDetails = guardianResults.find(
            (p) => p._id === guardian.patientId,
          );

          if (guardianDetails && guardianDetails.dob) {
            const guardianAge = calculateAge(guardianDetails.dob.split("T")[0]);

            if (guardianAge < 18) {
              return `${guardian.relationshipType} must be at least 18 years old`;
            }
            const ageGap = guardianAge - patientAge;
            if (Number(guardian.relationshipType) === 2 && ageGap < 12) {
              return "Parent must be at least 12 years older than patient";
            }
            if (Number(guardian.relationshipType) === 3 && ageGap < 5) {
              return "Guardian must be at least 5 years older than patient";
            }
          }
        } catch (error) {
          console.error("Error validating guardian age:", error);
        }
      }
    }

    return null;
  };

  const validateForm = async (
    formData: PatientFormData,
    givenNameLocal: string,
    familyNameLocal: string,
    currentPatientId?: string,
    searchFunction?: (pharmacyId: string, query: string) => Promise<any[]>,
    pharmacyId?: string,
  ): Promise<PatientFormErrors> => {
    const errors: PatientFormErrors = {};
    if (!formData.givenName?.trim()) {
      errors.givenName = "Given name is required";
    }
    if (!formData.familyName?.trim()) {
      errors.familyName = "Family name is required";
    }
    const dobError = validateDateOfBirth(formData.dateOfBirth);
    if (dobError) {
      errors.dateOfBirth = dobError;
    }

    const telephoneError = validateUKTelephone(formData.telephone);
    if (telephoneError) {
      errors.telephone = telephoneError;
    }

    const nhsError = validateNHSNumber(formData.nhsNumber);
    if (nhsError) {
      errors.nhsNumber = nhsError;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
    }

    // Address fields are optional — no validation required.

    const guardianError = await validateGuardianAge(
      formData.relationships,
      formData.dateOfBirth,
      searchFunction,
      pharmacyId,
    );
    if (guardianError) {
      errors.relationships = guardianError;
    }

    return errors;
  };

  const transformPatientFormDataToPayload = (
    formData,
    companyId,
    currentPharmacyId,
    givenNameLocal: string,
    familyNameLocal: string,
    existingPatient?: any,
    isFromPDS: boolean = false,
  ) => {
    const foreName = formData?.givenName.trim();
    const middleName = formData?.middleName?.trim() || "";
    const surName = formData?.familyName.trim();
    const formAny = formData as any;
    const selectedSurgery = formAny?.surgeryData || {};
    const selectedGpObj =
      formAny?.gpData ||
      selectedSurgery?.generalPractitioner ||
      selectedSurgery?.GeneralPractitioner ||
      null;
    const hasSurgery = Boolean(
      formAny?.surgeryData ||
        formAny?.surgeryName ||
        formAny?.surgeryAddress ||
        formAny?.surgeryPhone ||
        formAny?.surgeryEmail,
    );
    const resolvedPatientId =
      existingPatient?.patientID ||
      existingPatient?.patientId ||
      existingPatient?.id ||
      existingPatient?._id ||
      uuidv4();

    return {
      patientID: resolvedPatientId,
      _id: existingPatient?._id,
      title: formData?.title || "",
      foreName: foreName || "",
      middleName: middleName || "",
      surName: surName || "",
      dob: formData.dateOfBirth
        ? new Date(formData.dateOfBirth).toISOString()
        : "",
      sex:
        formData.gender === "Male"
          ? 1
          : formData.gender === "Female"
          ? 2
          : formData.gender === "Other"
          ? 3
          : 0,
      nhsNumber: formData.nhsNumber || "",
      isNHSNumberValid: false,
      contacts: formData.telephone
        ? [
            {
              patientContactId:
                existingPatient?.contacts?.[0]?.patientContactId || uuidv4(),
              contactType: 4,
              value: formData.telephone,
            },
          ]
        : [],
      currentPatientVitals: existingPatient?.currentPatientVitals,
      ethnicity: Number(formData.ethnicity) || 0,
      patientTags: [],
      additionalTags: formData.additionalTags || [],
      address: formData.address || "",
      email: formData.email || "",
      companyID: companyId,
      pharmacyID: currentPharmacyId,
      allergies: formData.allergies || [],
      conditionRelated: formData.conditions || [],
      medications: formData.medications || [],

      relationships: (formData.relationships || []).map((rel) => ({
        id: rel.id || null,
        isPrimary: rel.isPrimary || false,
        patientAddress: rel.patientAddress || "",
        patientEmail: rel.patientEmail || "",
        patientID: rel.patientId || "",
        patientName: rel.patientName || "",
        relationshipType: Number(rel.relationshipType),
      })),
      doctor: {
        gpName: formData.doctor?.gpName || "",
        surgeryName: formData.doctor?.surgeryName || "",
        surgeryAddress: formData.doctor?.surgeryAddress || "",
      },
      preferredAddress: {
        address: formData.preferredAddress,
      },
      surgery: hasSurgery
        ? {
            _id: selectedSurgery?._id || null,
            organisationCode:
              selectedSurgery?.organisationCode ||
              selectedSurgery?.OrganisationCode ||
              "",
            name:
              selectedSurgery?.name ||
              selectedSurgery?.Name ||
              formAny?.surgeryName ||
              formData?.doctor?.surgeryName ||
              "",
            addressLine1:
              selectedSurgery?.addressLine1 ||
              selectedSurgery?.AddressLine1 ||
              formAny?.surgeryAddress ||
              formData?.doctor?.surgeryAddress ||
              "",
            addressLine2:
              selectedSurgery?.addressLine2 ||
              selectedSurgery?.AddressLine2 ||
              "",
            addressLine3:
              selectedSurgery?.addressLine3 ||
              selectedSurgery?.AddressLine3 ||
              "",
            addressLine4:
              selectedSurgery?.addressLine4 ||
              selectedSurgery?.AddressLine4 ||
              "",
            addressLine5:
              selectedSurgery?.addressLine5 ||
              selectedSurgery?.AddressLine5 ||
              "",
            postcode:
              selectedSurgery?.postcode || selectedSurgery?.Postcode || "",
            contactTelephoneNumber: Array.isArray(formAny?.surgeryPhone)
              ? formAny?.surgeryPhone?.[0] || ""
              : formAny?.surgeryPhone ||
                (Array.isArray(selectedSurgery?.contactTelephoneNumber)
                  ? selectedSurgery?.contactTelephoneNumber?.[0] || ""
                  : selectedSurgery?.contactTelephoneNumber) ||
                (Array.isArray(selectedSurgery?.ContactTelephoneNumber)
                  ? selectedSurgery?.ContactTelephoneNumber?.[0] || ""
                  : selectedSurgery?.ContactTelephoneNumber) ||
                "",
            email: Array.isArray(formAny?.surgeryEmail)
              ? formAny?.surgeryEmail?.[0] || ""
              : formAny?.surgeryEmail ||
                (Array.isArray(selectedSurgery?.email)
                  ? selectedSurgery?.email?.[0] || ""
                  : selectedSurgery?.email) ||
                (Array.isArray(selectedSurgery?.Email)
                  ? selectedSurgery?.Email?.[0] || ""
                  : selectedSurgery?.Email) ||
                (Array.isArray(selectedSurgery?.emailAddress)
                  ? selectedSurgery?.emailAddress?.[0] || ""
                  : selectedSurgery?.emailAddress) ||
                "",
            generalPractitioner: selectedGpObj
              ? selectedGpObj
              : formData.doctorName
              ? { name: formData.doctorName }
              : null,
          }
        : null,
      ...(isFromPDS && { IsPDSEntry: true }),
    };
  };

  const handleInputChangeWrapper = <K extends keyof PatientFormData>(
    field: K,
    value: PatientFormData[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field in errors) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleEnableErrorFieldTab = (
    validationErrors: PatientFormErrors = errors,
  ) => {
    if (
      validationErrors.name ||
      validationErrors.dateOfBirth ||
      validationErrors.gender ||
      validationErrors.ethnicity ||
      validationErrors.nhsNumber ||
      validationErrors.address
    ) {
      setActiveTab("personal");
    } else if (validationErrors.telephone || validationErrors.email) {
      setActiveTab("contact");
    } else if (validationErrors.relationships) {
      setActiveTab("relationships");
    }
  };

  const handleSubmitWrapper = () => {
    // Create patient identity object with preserved IDs
    // All form values and relationships come from formData state (the edited values)
    const patientIdentity = {
      patientID: preservedPatientIdsRef.current?.patientID,
      _id: preservedPatientIdsRef.current?._id,
    };

    handleSubmit(
      formData, // All edited form values
      onSave,
      onOpenChange,
      resetFormWrapper,
      companyId,
      currentPharmacyId,
      patientIdentity, // Only IDs for identifying which patient to update
      setAddPatientFormMessage,
      setErrors,
      undefined,
      currentPharmacyId,
      givenNameLocal,
      familyNameLocal,
      isFromPDS,
    );
  };

  const handleSubmit = async (
    formData: PatientFormData,
    onSave: (patient: any) => void,
    onOpenChange: (open: boolean) => void,
    resetForm: () => void,
    companyId: string,
    currentPharmacyId: string,
    existingPatient?: any,
    setAddPatientFormMessage?: (val: string) => void,
    setErrors?: (errors: PatientFormErrors) => void,
    searchFunction?: (pharmacyId: string, query: string) => Promise<any[]>,
    pharmacyId?: string,
    givenNameLocal?: string,
    familyNameLocal?: string,
    isFromPDS?: boolean,
  ) => {
    try {
      const errors = await validateForm(
        formData,
        givenNameLocal,
        familyNameLocal, // ADD THIS
        existingPatient?._id,
        searchFunction,
        pharmacyId || currentPharmacyId,
      );
      if (Object.keys(errors).length > 0) {
        if (setErrors) {
          setErrors(errors);
        }
        handleEnableErrorFieldTab(errors);
        return;
      }

      if (setErrors) {
        setErrors({});
      }

      let finalPayload = transformPatientFormDataToPayload(
        formData,
        companyId,
        currentPharmacyId,
        givenNameLocal,
        familyNameLocal,
        existingPatient,
        isFromPDS ? true : undefined,
      );

      if (selectedCompanyTags && Array.isArray(selectedCompanyTags)) {
        finalPayload.additionalTags = selectedCompanyTags.map((tag) => ({
          tagId: tag.tagId,
          companyId: companyId,
          tagName: tag.tagName,
          tagDescription: tag.tagDescription || "",
          colour: tag.colour || "#999999",
          createdAt: tag.createdAt || new Date().toISOString(),
          status: tag.status ?? 1,
        }));
      }

      if (Array.isArray(finalPayload.allergies)) {
        finalPayload.allergies = finalPayload.allergies.map((a: any) => ({
          ...a,
          severity:
            typeof a.severity === "string"
              ? severityStringToNumber[
                  a.severity as "mild" | "moderate" | "severe"
                ]
              : a.severity,
        }));
      }

      if (Array.isArray(finalPayload.conditionRelated)) {
        finalPayload.conditionRelated = finalPayload.conditionRelated.map(
          (c: any) => {
            return {
              ...c,
              status: statusStringToNumber(c.status),
            };
          },
        );
      }

      if (Array.isArray(finalPayload.medications)) {
        finalPayload.medications = finalPayload.medications.map((c: any) => ({
          ...c,
          therapyLevel:
            typeof c.therapyLevel === "string"
              ? therapyLevelStringToNumber[
                  c.therapyLevel as
                    | "acute"
                    | "chronic"
                    | "maintenance"
                    | "asneeded"
                ]
              : c.therapyLevel,
          measure: 1,
        }));
      }

      if (existingPatient?._id) {
        const response = await updatePatientDetails(finalPayload);
        if (response !== "true") {
          if (setAddPatientFormMessage) {
            setAddPatientFormMessage(response || "Unknown error");
          }
          return;
        }

        if (finalPayload?.preferredAddress?.address != null) {
          let AddressLine1 = "";
          const {
            houseName,
            streetName,
            town,
            county,
            postcode,
          } = finalPayload.preferredAddress.address;

          AddressLine1 = [houseName, streetName, town, county, postcode]
            .filter((x) => x && x.trim() !== "")
            .join(", ");

          finalPayload.address = AddressLine1;
          finalPayload.preferredAddress.address = {
            ...finalPayload.preferredAddress.address,
            addressLine2: finalPayload.preferredAddress.address.streetName,
          };
        }

        const updatedPatientDetails = {
          ...finalPayload,
        };

        setSelectedPatientDetails(updatedPatientDetails);
      } else {
        const response = await addNewPatient(finalPayload);
        const patientResponse = response?.patientDetailsModel;
        if (response.message !== "true") {
          if (setAddPatientFormMessage) {
            setAddPatientFormMessage(response?.message || "Unknown error");
          }
          return;
        }
        if (patientResponse) {
          if (nhsEmergencyConsultationId && patientResponse?.patientID) {
            const matchedConsultationId = nhsEmergencyConsultationId;

            await updateConsultationForPatientMatch({
              consultationId: matchedConsultationId,
              patientId: patientResponse.patientID,
            });
            setNhsEmergencyConsultationId("");

            await IntercomTrack(
              currentUser,
              currentPharmacy,
              company,
              intercomActionPatientAddText,
            );
            closeModal();
            await navigateToNhsConsultation(
              patientResponse.patientID,
              matchedConsultationId,
            );
            onSave(patientResponse);
            onOpenChange(false);
            resetForm();
            return;
          }

          await IntercomTrack(
            currentUser,
            currentPharmacy,
            company,
            intercomActionPatientAddText,
          );
          closeModal();
          if (currentPharmacyId) {
            setIsPatientDashboard(true);
            setSelectedPatientDetails(patientResponse);

            navigate(
              `patientdashboard-v2/${currentPharmacyId}/${patientResponse?.patientID}`,
            );
            const payloadPatient = {
              keyword: "",
              practitioners: "",
              treatmentPlan: "",
              isTitanSearch: false,
              consultationStatus: 0,
              pharmacyID: currentPharmacyId,
              companyId: companyId,
            };
            await getPatientsListV2(payloadPatient);
          }
        } else {
          console.error("Failed to add patient");
        }
      }

      onSave(finalPayload);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error("Error submitting patient form:", error);
      if (setAddPatientFormMessage) {
        setAddPatientFormMessage("An error occurred while saving patient data");
      }
    }
  };

  // Form Reset Logic
  const resetForm = (
    setFormData: (data: PatientFormData) => void,
    setNewMedication: (value: string) => void,
    setNewTag: (value: string) => void,
  ) => {
    setFormData(getInitialFormData());
    setNewMedication("");
    setNewTag("");
  };
  const resetFormWrapper = () => {
    resetForm(setFormData, setNewMedication, setNewTag);
    setActiveTab("personal"); // Reset to first tab
    setErrors({});
  };

  //Title Options
  // Title Options
  const titleOptions = [
    { value: "Master", label: "Master" },
    { value: "Mr", label: "Mr" },
    { value: "Miss", label: "Miss" },
    { value: "Mrs", label: "Mrs" },
    { value: "Ms", label: "Ms" },
    { value: "Captain", label: "Captain" },
    { value: "Dr", label: "Dr" },
    { value: "Prof", label: "Prof" },
    { value: "Rev", label: "Rev" },
    { value: "Mx", label: "Mx" },
    { value: "Sir", label: "Sir" },
    { value: "Dame", label: "Dame" },
    { value: "Cllr", label: "Cllr" },
    { value: "Lady", label: "Lady" },
    { value: "Lord", label: "Lord" },
    { value: "Father", label: "Father" },
    { value: "Doctor", label: "Doctor" },
    { value: "Earl", label: "Earl" },
    { value: "Sister", label: "Sister" },
    { value: "Hon.", label: "Hon." },
  ];

  // Gender Options
  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
    { value: "Prefer not to say", label: "Prefer not to say" },
  ];

  // Use the ethnicityTypes enum to create options
  const ethnicityOptions = Object.entries(ethnicityTypes).map(
    ([key, label]) => ({
      value: key,
      label: label,
    }),
  );

  // Severity Color Classes
  const getSeverityColorClass = (severity: string) => {
    switch (severity) {
      case "severe":
        return "bg-red-100 text-red-700";
      case "moderate":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-green-100 text-green-700";
    }
  };

  // Status Color Classes
  const getStatusColorClass = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-700";
      case "chronic":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  // Therapy Level Color Classes
  const getTherapyLevelColorClass = (therapyLevel: string) => {
    switch (therapyLevel) {
      case "acute":
        return "bg-red-100 text-red-700";
      case "chronic":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-blue-100 text-blue-700";
    }
  };

  const handleSaveAllergyWrapper = (newAllergy: any) => {
    handleSaveAllergy(newAllergy, formData, setFormData);
  };

  const handleEditAllergyWrapper = (id: string, updatedAllergy: any) => {
    handleEditAllergy(id, updatedAllergy, formData, setFormData);
  };

  const handleDeleteAllergyWrapper = (name: string) => {
    handleDeleteAllergy(name, formData, setFormData);
  };
  const handleCloseWarningPopup = () => {
    // setIsNotificationModalOpen(false)
  };

  const handleSaveConditionWrapper = (newCondition: any) => {
    handleSaveCondition(newCondition, formData, setFormData);
  };

  const handleEditConditionWrapper = (id: string, updatedCondition: any) => {
    handleEditCondition(id, updatedCondition, formData, setFormData);
  };

  const handleDeleteConditionWrapper = (conditionName: string) => {
    handleDeleteCondition(conditionName, formData, setFormData);
  };

  const handleSaveMedicationWrapper = (newMedication: any) => {
    handleSaveMedication(newMedication, formData, setFormData);
  };

  const handleEditMedicationWrapper = (id: string, updatedMedication: any) => {
    handleEditMedication(id, updatedMedication, formData, setFormData);
  };

  const handleDeleteMedicationWrapper = (itemName: string) => {
    handleDeleteMedication(itemName, formData, setFormData);
  };

  const requiresConsent = useMemo(() => requiresGuardianConsent(formData), [
    formData.dateOfBirth,
  ]);
  const hasGuardian = useMemo(() => hasParentOrGuardian(formData), [
    formData.relationships,
  ]);

  const patientAge = useMemo(() => calculateAge(formData.dateOfBirth), [
    formData.dateOfBirth,
  ]);

  const confirmDelete = (action: () => void, message?: string) => {
    setDeleteAction(() => action);
    if (message) setDeleteMessage(message);
    setDeleteModalOpen(true);
  };

  // SEPARATE effect to ONLY capture patient ID once when modal first opens in edit mode
  useEffect(() => {
    if (
      editMode &&
      open &&
      !isInitializing.current &&
      !preservedPatientIdsRef.current?.patientID
    ) {
      // Only capture IDs if they haven't been captured yet in this session
      const capturedIDs = {
        patientID: editMode?.patientID,
        _id: editMode?._id,
      };

      // Only set if we have valid IDs to preserve
      if (capturedIDs.patientID || capturedIDs._id) {
        preservedPatientIdsRef.current = capturedIDs;
      }
    }
  }, [open]); // ONLY depend on open - NOT on editMode!

  useEffect(() => {
    let userTelephone = "";
    if (editMode && open && !isInitializing.current) {
      if (
        editMode?.contacts !== null &&
        editMode?.contacts?.[0]?.value != null &&
        editMode?.contacts?.[0]?.value.trim() !== ""
      ) {
        userTelephone = editMode?.contacts?.[0]?.value;
      } else if (
        editMode?.preferredContact?.contactType === 2 ||
        editMode?.preferredContact?.contactType === 4
      ) {
        if (editMode?.preferredContact?.value.trim() !== "") {
          userTelephone = editMode.preferredContact.value;
        }
      }
      setCurrentStep("form-entry");
      isInitializing.current = true;
      getCompanyTags(companyId);
      setFormData({
        title: editMode.title,
        name:
          editMode.patientName ||
          `${editMode.foreName || ""} ${editMode.middleName ||
            ""} ${editMode.surName || ""}`.trim() ||
          "",
        givenName: editMode.foreName,
        familyName: editMode.surName,
        dateOfBirth: editMode.dob ? editMode.dob.split("T")[0] : "",
        gender:
          editMode.sex === 1
            ? "Male"
            : editMode.sex === 2
            ? "Female"
            : editMode.sex === 3
            ? "Other"
            : "",
        nhsNumber: editMode.nhsNumber || "",
        telephone: userTelephone,
        email: editMode.email || "",
        address: editMode.address || "",
        ethnicity: editMode.ethnicity ? String(editMode.ethnicity) : "",
        allergies: editMode.allergies || [],
        conditions: editMode.conditionRelated || [],
        medications: editMode.medications || [],
        patientTags: editMode.patientTags || [],
        additionalTags: editMode.additionalTags || [],
        doctor: {
          gpName: editMode.doctor?.gpName || "",
          surgeryName: editMode.doctor?.surgeryName || "",
          surgeryAddress: editMode.doctor?.surgeryAddress || "",
        },

        relationships: editMode.relationships || [],
        preferredAddress: editMode?.preferredAddress?.address || null,
        surgeryData: editMode.surgery || null,
        surgeryName:
          editMode.surgery?.name || editMode.doctor?.surgeryName || "",
        surgeryAddress:
          editMode.surgery?.addressLine1 ||
          editMode.doctor?.surgeryAddress ||
          "",
        surgeryPhone: Array.isArray(editMode.surgery?.contactTelephoneNumber)
          ? editMode.surgery?.contactTelephoneNumber?.[0] || ""
          : editMode.surgery?.contactTelephoneNumber || "",
        surgeryEmail: Array.isArray(editMode.surgery?.email)
          ? editMode.surgery?.email?.[0] || ""
          : editMode.surgery?.email || "",
        gpData: editMode.surgery?.generalPractitioner || null,
        doctorName:
          editMode.surgery?.generalPractitioner?.name ||
          editMode.doctor?.gpName ||
          "",
      });
      setSelectedCompanyTags(editMode.additionalTags);
      setTimeout(() => {
        isInitializing.current = false;
      }, 100);
    } else if (!open) {
      isInitializing.current = false;
      preservedPatientIdsRef.current = {}; // CLEAR preserved IDs
      setNhsEmergencyConsultationId("");
      setErrors({});
    }
  }, [editMode, open, setFormData]);

  useEffect(() => {
    if (patientSearchQuery.length < 3) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await getPatientByKeyword(
          currentPharmacyId,
          patientSearchQuery,
          companyId,
        );

        const formattedResult = result
          .map((patient) => ({
            ...patient,
            dob: patient?.dob ? dayjs(patient.dob).format("DD/MM/YYYY") : "",
            age: patient?.dob ? getAge(patient?.dob) : "",
            isUnder16: getAge(patient?.dob) < 16 ? true : false,
          }))
          .filter((patient) => patient.patientID !== editMode?.patientID);
        setSearchResults(formattedResult || []);
        setShowSearchResults(true);
      } catch (err) {
        console.error("Patient search error", err);
        setSearchResults([]);
        setShowSearchResults(true);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [patientSearchQuery, currentPharmacyId]);

  // ---------- PDS handlers (copied/adapted from builder) ----------
  // const handlePDSNameSearch = async () => {
  //   if (!pdsGivenName.trim() || !pdsFamilyName.trim() || !pdsGender || !pdsDOB) {
  //     setPDSSearchError("Please enter Given Name, Family Name, Gender, and Date of Birth");
  //     return;
  //   }

  //   setIsPDSSearching(true);
  //   setPDSSearchError("");
  //   setPDSSearchResults([]);
  //   try {
  //     const results = await searchPDSByName({
  //       givenName: pdsGivenName,
  //       familyName: pdsFamilyName,
  //       gender: pdsGender,
  //       dateOfBirth: pdsDOB,
  //       postcode: pdsPostcode || undefined,
  //     });

  //     if (!results || results.length === 0) {
  //       setPDSSearchError("No patient found in PDS. You can add them manually.");
  //     } else {
  //       setPDSSearchResults(results);
  //     }
  //   } catch (error) {
  //     console.error("PDS search error:", error);
  //     setPDSSearchError("Error searching PDS. Please try again.");
  //   } finally {
  //     setIsPDSSearching(false);
  //   }
  // };

  const handlePDSNameSearch = async () => {
    if (
      !pdsGivenName.trim() ||
      !pdsFamilyName.trim() ||
      !pdsGender ||
      !pdsDOB
    ) {
      setPDSSearchError(
        "Please enter Given Name, Family Name, Gender, and Date of Birth",
      );
      return;
    }

    setIsPDSSearching(true);
    setPDSSearchError("");
    setPDSSearchResults([]);

    const payload = {
      pharmacyId: currentPharmacyId,
      givenName: pdsGivenName,
      familyName: pdsFamilyName,
      gender:
        pdsGender === "Male"
          ? 1
          : pdsGender === "Female"
          ? 2
          : pdsGender === "Other"
          ? 3
          : 0,
      dateOfBirth: pdsDOB,
      postcode: pdsPostcode,
    };

    try {
      const results = await searchByDemographics(payload);

      if (!results || results.length === 0) {
        setPDSSearchError(
          "No patient found in PDS. You can add them manually.",
        );
      } else {
        setPDSSearchResults([results]);
      }
    } catch (error) {
      console.error("PDS search error:", error);
      setPDSSearchError("Error searching PDS. Please try again.");
    } finally {
      setIsPDSSearching(false);
    }
  };

  // const handlePDSNHSSearch = async () => {
  //   if (!pdsNHSNumber.trim() || !pdsNHSDOB) {
  //     setPDSSearchError("Please enter NHS Number and Date of Birth");
  //     return;
  //   }

  //   if (!validateNHSNumber(pdsNHSNumber)) {
  //     setPDSSearchError("Invalid NHS Number format (must be 10 digits)");
  //     return;
  //   }

  //   setIsPDSSearching(true);
  //   setPDSSearchError("");
  //   setPDSSearchResults([]);

  //   try {
  //     const result = await searchPDSByNHS({
  //       nhsNumber: pdsNHSNumber.replace(/\s/g, ""),
  //       dateOfBirth: pdsNHSDOB,
  //     });

  //     if (!result) {
  //       setPDSSearchError("No patient found in PDS. Please check the details or add manually.");
  //     } else {
  //       setPDSSearchResults([result]);
  //     }
  //   } catch (error) {
  //     console.error("PDS search error:", error);
  //     setPDSSearchError("Error searching PDS. Please try again.");
  //   } finally {
  //     setIsPDSSearching(false);
  //   }
  // };

  const handlePDSNHSSearch = async () => {
    if (!pdsNHSNumber.trim() || !pdsNHSDOB) {
      setPDSSearchError("Please enter NHS Number and Date of Birth");
      return;
    }

    const nhsError = validateNHSNumber(pdsNHSNumber.replace(/\s/g, ""));
    if (nhsError) {
      setPDSSearchError(
        nhsError || "Invalid NHS Number format (must be 10 digits)",
      );
      return;
    }

    setIsPDSSearching(true);
    setPDSSearchError("");
    setPDSSearchResults([]);

    const payload = {
      pharmacyId: currentPharmacyId,
      nhsNumber: pdsNHSNumber.replace(/\s/g, ""),
      dateOfBirth: pdsNHSDOB,
    };

    try {
      const result = await searchByNhsNumber(payload);

      if (!result || result.length === 0) {
        setPDSSearchError(
          "No patient found in PDS. Please check details or add manually.",
        );
      } else {
        // API might return single or multiple; normalize to array
        setPDSSearchResults(Array.isArray(result) ? result : [result]);
      }
    } catch (error) {
      console.error("PDS search error:", error);
      setPDSSearchError("Error searching PDS. Please try again.");
    } finally {
      setIsPDSSearching(false);
    }
  };

  const handleSelectPDSPatient = (patient: PDSPatient) => {
    // Map PDS fields (foreName, middleName, surName, dob, sex, contacts, doctor) to our form shape
    const foreName =
      (patient as any).foreName || (patient as any).givenName || "";
    const middleName = (patient as any).middleName || "";
    const surName =
      (patient as any).surName || (patient as any).familyName || "";
    const combinedName = [foreName, middleName, surName]
      .filter(Boolean)
      .join(" ");

    // DOB - convert ISO to yyyy-mm-dd for input[type=date]
    const rawDob = (patient as any).dob || (patient as any).dateOfBirth || "";
    const dateOfBirth = rawDob
      ? String(rawDob).includes("T")
        ? String(rawDob).split("T")[0]
        : String(rawDob)
      : "";

    // Gender mapping (PDS uses numbers)
    const gender =
      (patient as any).sex === 1
        ? "Male"
        : (patient as any).sex === 2
        ? "Female"
        : (patient as any).sex === 3
        ? "Other"
        : "";

    // Telephone: pick first contact with a non-empty value
    let telephone = "";
    if (Array.isArray((patient as any).contacts)) {
      const firstContact = (patient as any).contacts.find(
        (c: any) => c && c.value && String(c.value).trim() !== "",
      );
      if (firstContact) telephone = String(firstContact.value).trim();
    }

    const email = (patient as any).email || "";

    const address = (patient as any).address || "";
    const preferredAddress = (patient as any).preferredAddress?.address || null;
    // Doctor info might be nested under doctor or top-level fields
    const doctorObj = (patient as any).doctor || {};
    const gpName =
      doctorObj.gpName ||
      (patient as any).doctorName ||
      (patient as any).gpName ||
      "";
    const surgeryName =
      doctorObj.surgeryName ||
      (patient as any).surgeryName ||
      (patient as any).p_gsurgery ||
      "";
    const surgeryAddress =
      doctorObj.surgeryAddress ||
      (patient as any).surgeryAddress ||
      (patient as any).p_gsurgeryAddress ||
      "";
    const pdsTitle = patient?.title || "";
    const formattedTitle =
      pdsTitle.charAt(0).toUpperCase() + pdsTitle.slice(1).toLowerCase();
    setFormData({
      ...formData,
      name: combinedName,
      givenName: foreName,
      middleName,
      familyName: surName,
      dateOfBirth,
      gender,
      nhsNumber: normalizeNhsNumber((patient as any).nhsNumber),
      address,
      telephone: telephone ? telephone : patient?.telephone || "",
      email,
      ethnicity:
        (patient as any).ethnicity != null
          ? String((patient as any).ethnicity)
          : "",
      allergies: (patient as any).allergies || [],
      conditions: (patient as any).conditionRelated || [],
      medications: (patient as any).medications || [],
      patientTags: (patient as any).patientTags || [],
      additionalTags: (patient as any).additionalTags || [],
      doctor: {
        gpName: gpName,
        surgeryName: surgeryName,
        surgeryAddress: surgeryAddress,
      },
      relationships: (patient as any).relationships || [],
      title: formattedTitle,
      preferredAddress: preferredAddress,
    });
    // If there's an exposed selected-company-tags binding for edit flow, keep it in sync
    try {
      // setSelectedCompanyTags exists in hook state - update if available and patient provides additionalTags
      if (
        Array.isArray((patient as any).additionalTags) &&
        typeof setSelectedCompanyTags === "function"
      ) {
        setSelectedCompanyTags((patient as any).additionalTags);
      }
    } catch (e) {
      // ignore if not available
    }

    setIsFromPDS(true);
    setCurrentStep("form-entry");
  };

  const handleSkipPDSSearch = () => {
    // Prefill form data from PDS search inputs
    if (pdsGivenName || pdsFamilyName || pdsGender || pdsDOB) {
      setFormData((prev) => ({
        ...prev,
        givenName: pdsGivenName,
        familyName: pdsFamilyName,
        gender: pdsGender,
        dateOfBirth: pdsDOB,
        nhsNumber: prev.nhsNumber || pdsNHSNumber.replace(/\s/g, ""),
        address: pdsPostcode ? prev.address : prev.address, // Keep existing address logic
      }));
    }
    setCurrentStep("form-entry");
  };

  const handleBackToPDSSearch = () => {
    setCurrentStep("pds-search");
    setPDSSearchResults([]);
    setPDSSearchError("");
  };

  const resetPDSState = () => {
    setCurrentStep("pds-search");
    setPDSSearchMode("name");
    setPDSSearchResults([]);
    setPDSSearchError("");
    setPDSGivenName("");
    setPDSFamilyName("");
    setPDSGender("");
    setPDSDOB("");
    setPDSPostcode("");
    setPDSNHSNumber("");
    setPDSNHSDOB("");
    setIsVerifying(false);
    setVerificationStatus(null);
    setVerificationMessage("");
    setIsVerified(false);
  };

  const searchByDemographics = async (data: {
    pharmacyId: string;
    givenName: string;
    familyName: string;
    gender: any;
    dateOfBirth: string;
    postcode?: string;
  }) => {
    try {
      const resp = await getPatientByDemoGraphics(data);
      return resp;
    } catch (err) {
      console.error("Error fetching patient by demographics:", err);
      return null;
    }
  };

  const searchByNhsNumber = async (data: {
    pharmacyId: string;
    nhsNumber: string;
    dateOfBirth: string;
  }) => {
    try {
      const resp = await getPatientByNhsNumber(data);
      return resp;
    } catch (err) {
      console.error("Error fetching patient by NHS:", err);
      return null;
    }
  };

  const normalizeNhsNumber = (value: any): string => {
    if (!value) return "";

    return String(value)
      .replace(/^NHS\|/i, "")
      .trim();
  };

  const handleAddNewRelatedPatient = async (patientPayload: {
    foreName: string;
    surName: string;
    dateOfBirth: string;
    email: string;
    telephone: string;
  }) => {
    const newPatientPayload = {
      patientID: uuidv4(),
      title: "",
      foreName: patientPayload.foreName.trim(),
      middleName: "",
      surName: patientPayload.surName.trim(),
      dob: patientPayload.dateOfBirth
        ? new Date(patientPayload.dateOfBirth).toISOString()
        : "",
      sex: 0,
      nhsNumber: "",
      isNHSNumberValid: false,
      contacts: patientPayload.telephone
        ? [
            {
              patientContactId: uuidv4(),
              contactType: 4,
              value: patientPayload.telephone,
            },
          ]
        : [],
      ethnicity: 0,
      patientTags: [],
      additionalTags: [],
      address: "",
      email: patientPayload.email || "",
      companyID: companyId,
      pharmacyID: currentPharmacyId,
      allergies: [],
      conditionRelated: [],
      medications: [],
      relationships: [],
      doctor: { gpName: "", surgeryName: "", surgeryAddress: "" },
      preferredAddress: null,
    };

    const response = await addNewPatient(newPatientPayload);
    const created = response?.patientDetailsModel;
    if (!created || response?.message !== "true") {
      throw new Error(response?.message || "Failed to add related patient");
    }
    return created;
  };

  return {
    darkMode,
    formData,
    setFormData,
    activeTab,
    setActiveTab,
    errors,
    updatePatientDetailsLoader,
    addPatientLoader,
    addPatientFormMessage,
    selectedPatient,
    setSelectedPatient,
    selectedRelationshipType,
    setSelectedRelationshipType,
    addRelationship,
    removeRelationship,
    handleInputChangeWrapper,
    handleSubmitWrapper,
    resetFormWrapper,
    genderOptions,
    titleOptions,
    relationshipOptions,
    ethnicityOptions,
    requiresConsent,
    hasGuardian,
    patientAge,
    patientSearchQuery,
    setPatientSearchQuery,
    searchResults,
    isSearching,
    showSearchResults,
    setShowSearchResults,
    showPatientDetailsEditModal,
    getSeverityColorClass,
    getStatusColorClass,
    statusMap,
    getTherapyLevelColorClass,
    therapyLevelMap,
    handleSaveAllergyWrapper,
    handleEditAllergyWrapper,
    handleDeleteAllergyWrapper,
    handleSaveConditionWrapper,
    handleEditConditionWrapper,
    handleDeleteConditionWrapper,
    handleSaveMedicationWrapper,
    handleEditMedicationWrapper,
    handleDeleteMedicationWrapper,
    handleDoctorSearch,
    searchedDoctors,
    deleteModalOpen,
    setDeleteModalOpen,
    deleteAction,
    deleteMessage,
    confirmDelete,
    tagsOptions,
    setTagsOptions,
    isCompanyTagsLoading,
    toggleTagsPickerDropdown,
    isOpenTagsPickerDropdown,
    setIsOpenTagsPickerDropdown,
    selectedCompanyTags,
    tagFilterValue,
    setTagFilterValue,
    generateRandomTagsColor,
    previewTagColor,
    setPreviewTagColor,
    handleSelectTag,
    handleAddCompanyTag,
    currentTag,
    setCurrentTag,
    handleDeleteTag,
    editingTag,
    setEditingTag,
    //PDS
    handlePDSNameSearch,
    handlePDSNHSSearch,
    handleSelectPDSPatient,
    handleSkipPDSSearch,
    handleBackToPDSSearch,
    resetPDSState,
    setPDSSearchMode,
    setPDSSearchError,
    setPDSSearchResults,
    setPDSGivenName,
    setPDSFamilyName,
    setPDSGender,
    setPDSDOB,
    setPDSPostcode,
    setPDSNHSNumber,
    setPDSNHSDOB,
    currentStep,
    pdsSearchMode,
    pdsGivenName,
    pdsFamilyName,
    pdsGender,
    pdsDOB,
    pdsPostcode,
    isPDSSearching,
    pdsNHSNumber,
    pdsNHSDOB,
    pdsSearchError,
    pdsSearchResults,
    givenNameLocal,
    setGivenNameLocal,
    familyNameLocal,
    setFamilyNameLocal,
    searchByDemographics,
    searchByNhsNumber,
    companyId,
    currentPharmacyId,
    handleAddNewRelatedPatient,
    searchedSurgeries,
    surgeryData,
  };
};
